import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

/* ═══════════════════════════════════════════
   EDITABLE: FAQ data
   - Edit question and answer for each FAQ item
═══════════════════════════════════════════ */
const faqs = [
  {
    question: 'What is SWP (Systematic Withdrawal Plan)?',
    answer: 'A Systematic Withdrawal Plan allows you to withdraw a fixed sum of money at regular intervals from your mutual fund investment. According to inflation-calculated returns, a fixed sum is credited to your bank account for expenses after retirement.',
  },
  {
    question: 'How can I systematically manage my home loan?',
    answer: 'This broadly depends on the loan amount, tenure, and rate of interest. We can plan and schedule your EMIs in such a way that you can repay the loan before the end of the tenure and hence manage future liability effectively.',
  },
  {
    question: 'How much should a young investor invest?',
    answer: 'Generally, we recommend the 30-30-30 rule for allocation. For the younger generation, a 40-30-20 split works better — allocating more towards growth-oriented investments while maintaining adequate liquidity.',
  },
  {
    question: 'What is the tax liability on Mutual Funds & Stocks?',
    answer: 'For equity mutual funds, LTCG above Rs 1.25 lakh is taxed at 12.5%. Debt funds are taxed as per your income tax slab. We also help with comprehensive tax planning to minimize your overall liability.',
  },
  {
    question: 'How much insurance do I need?',
    answer: 'It depends on your current lifestyle plus loans/liabilities if any. Insurance is like an umbrella for a young family. We recommend coverage of at least 10-15x your annual income plus outstanding liabilities.',
  },
  {
    question: 'What is passive vs active style of investing?',
    answer: 'As India is a developing economy with growth across sectors, active investing tends to outperform. In developed economies like the USA, passive investing through index funds is more popular. We help determine the right mix for your goals.',
  },
  {
    question: 'Do you guide investments for NRIs?',
    answer: 'Yes, we guide NRIs for investing in India. We provide pathway guidance on how to invest through NRE/NRO accounts and explain the benefits and tax implications for non-resident investors.',
  },
];

export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section data-testid="faq-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#D4AF37] mb-4">
            Frequently Asked Questions
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0A192F]">
            Got Questions? We Have Answers
          </h2>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              data-testid={`faq-item-${index}`}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                data-testid={`faq-toggle-${index}`}
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#F8F9FA] transition-colors"
              >
                <span className="font-['Outfit'] text-sm sm:text-base font-medium text-[#0A192F] pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  size={20}
                  className={`text-[#D4AF37] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-5"
                >
                  <p className="text-sm text-[#49566B] leading-relaxed font-['Outfit']">
                    {faq.answer}
                  </p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
